import AnonymousUserService from "./anonymousUser.class.js";
import { isOpenedFromApp } from "./app.helpers.js";
import {
  eraseCookie,
  getCookie,
  getCookieNames,
  setCookie,
} from "./cookies.helpers.js";
import { isDevelopment } from "./environment.helpers.js";
import { getUtmParam } from "./params.helpers.js";
import { getApiUrl } from "./urls.helpers.js";

const COOKIE_CONSENT_NAME = "cookie_consent_selected";

const COOKIE_CONSENT_VALUES = {
  ALL: "all",
  NONE: "none",
};

// How long a banner decision is kept before the visitor is asked again.
const COOKIE_CONSENT_TTL_DAYS = 120;

// Marks consent cookies written with COOKIE_CONSENT_TTL_DAYS. Cookies without
// it predate the 120-day TTL (they were written for a year) and are shortened
// on the next visit. felmo.de (cms) writes the same pair on .felmo.de, so keep
// both in step.
const COOKIE_CONSENT_VERSION_NAME = "cookie_consent_version";
const COOKIE_CONSENT_VERSION = "2";

// First-party cookies the tracking tags set, by consent category, matched by
// name prefix. The ones without consent are deleted on a decision, so a
// withdrawn consent does not leave identifiers behind. felmo.de (cms) keeps
// the same lists, so keep both in step.
const TRACKING_COOKIE_PREFIXES = {
  // Google Analytics, Hotjar
  analytics: ["_ga", "_gid", "_gat", "_hj"],
  // Google Ads, Meta, TikTok, LinkedIn
  marketing: [
    "_gcl_",
    "_fbp",
    "_fbc",
    "_ttp",
    "_tt_",
    "ttcsid",
    "li_fat_id",
    "_li",
  ],
};

// Footer link (and deep link) that reopens the banner for a visitor who has
// already decided. felmo.de (cms) listens for the same anchor, so keep both
// in step.
const COOKIE_SETTINGS_HASH = "#cookie-einstellungen";

const COOKIE_BANNER = "cookie-banner";
const COOKIE_BANNER_PREFS = "cookie-banner-prefs";
const COOKIE_BANNER_PREFS_FORM = "cookie-banner-prefs-form";

const COOKIE_BANNER_BUTTONS = {
  ACCEPT: "cookie-banner-accept-btn",
  DECLINE: "cookie-banner-decline-btn",
  PREFS: "cookie-banner-prefs-btn",
  PREFS_ACCEPT: "cookie-banner-prefs-accept-btn",
  PREFS_DECLINE: "cookie-banner-prefs-decline-btn",
  PREFS_SAVE: "cookie-banner-prefs-save-btn",
};

const COOKIE_BANNER_CLOSE_BUTTON = "cookie-banner-close-btn";

const cookieBanner = document.getElementById(COOKIE_BANNER);
const cookieBannerPrefs = document.getElementById(COOKIE_BANNER_PREFS);

// The decision already stored when the banner was reopened from the cookie
// settings link: the consent cookie value and Shopify's analytics/marketing
// consent. `null` while the visitor is asked for the first time.
let previousConsent = null;

async function storeConsentShown(details = null) {
  const anonymousUserId = await AnonymousUserService.isReady(() => {
    return AnonymousUserService.getAuid();
  });

  const payload = {
    anonymous_user_id: anonymousUserId,
    details,
    domain: window.location.hostname,
    event: "cookie_consent_shown",
    value: null,
  };

  try {
    const url = getApiUrl() + "/v1/cookie-banner-trackings";

    await fetch(url, {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });

    console.info("[Cookie Banner] - Successfully storeConsentShown", payload);
  } catch (error) {
    const errorMessage = "Failed to storeConsentShown";

    console.error(`[Cookie Banner] - ${errorMessage}`, payload);

    Sentry.captureException(error, {
      extra: { message: errorMessage, payload },
    });
  }
}

async function storeConsentSelected(isAccepted, details = undefined) {
  const anonymousUserId = await AnonymousUserService.isReady(() => {
    return AnonymousUserService.getAuid();
  });

  const reopenedConsent = previousConsent;

  const payload = {
    anonymous_user_id: anonymousUserId,
    details: reopenedConsent
      ? { ...details, previous: reopenedConsent.value, reopened: true }
      : details,
    domain: window.location.hostname,
    event: "cookie_consent_selected",
    value: isAccepted ? COOKIE_CONSENT_VALUES.ALL : COOKIE_CONSENT_VALUES.NONE,
  };

  try {
    const url = getApiUrl() + "/v1/cookie-banner-trackings";

    await fetch(url, {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });

    storeCookieConsent(payload.value);

    const isPrefsSave = details?.target === COOKIE_BANNER_BUTTONS.PREFS_SAVE;

    const prefs = {
      analytics: isPrefsSave ? details?.prefs?.statistics : isAccepted,
      marketing: isPrefsSave ? details?.prefs?.marketing : isAccepted,
    };

    clearTrackingCookies(prefs);

    // The tags of the old decision are still running on this page. Reload so
    // the page comes back with them loaded (or left out) for the new one, once
    // Shopify has stored the new consent.
    const isChanged =
      reopenedConsent &&
      (reopenedConsent.value !== payload.value ||
        reopenedConsent.analytics !== !!prefs.analytics ||
        reopenedConsent.marketing !== !!prefs.marketing);

    setShopifyConsent(prefs, () => {
      if (isChanged) {
        window.location.reload();
      }
    });

    console.info(
      "[Cookie Banner] - Successfully storeConsentSelected",
      payload,
    );
  } catch (error) {
    const errorMessage = "Failed to storeConsentSelected";

    console.error(`[Cookie Banner] - ${errorMessage}`, payload);

    Sentry.captureException(error, {
      extra: {
        message: errorMessage,
        payload,
      },
    });
  } finally {
    closeBanner();
  }
}

// Deletes the tracking cookies of each category without consent. A tag may
// have written one on .felmo.de or on the current host, and a cookie only goes
// away when erased with the domain it was set with, so each one is erased on
// both as well as host-only. Cookies set HttpOnly by a server can't be reached.
function clearTrackingCookies(prefs) {
  const prefixes = [
    ...(prefs.analytics ? [] : TRACKING_COOKIE_PREFIXES.analytics),
    ...(prefs.marketing ? [] : TRACKING_COOKIE_PREFIXES.marketing),
  ];
  const domains = [undefined, ".felmo.de", window.location.hostname];

  getCookieNames()
    .filter((name) => prefixes.some((prefix) => name.startsWith(prefix)))
    .forEach((name) => {
      domains.forEach((domain) => eraseCookie(name, domain));
    });
}

// Stores the banner decision on .felmo.de, shared with felmo.de. Later visits
// don't prolong it: once it expires the banner is shown again.
function storeCookieConsent(value) {
  const config = {
    days: COOKIE_CONSENT_TTL_DAYS,
    domain: isDevelopment() ? undefined : ".felmo.de",
  };

  setCookie(COOKIE_CONSENT_NAME, value, config);
  setCookie(COOKIE_CONSENT_VERSION_NAME, COOKIE_CONSENT_VERSION, config);
}

// Re-writes consent cookies from before the 120-day TTL, so they expire at
// most 120 days from now instead of a year after the decision.
function migrateLegacyCookieConsent() {
  const value = getCookie(COOKIE_CONSENT_NAME);

  if (
    !value ||
    getCookie(COOKIE_CONSENT_VERSION_NAME) === COOKIE_CONSENT_VERSION
  ) {
    return;
  }

  storeCookieConsent(value);
}

function hideBanner() {
  cookieBanner?.style.setProperty("display", "none");
}

function showBanner() {
  cookieBanner?.style.setProperty("display", "block");
  document.body.style.setProperty("overflow", "hidden");
}

function hidePrefs() {
  cookieBannerPrefs?.style.setProperty("display", "none");
}

// Hides the banner but keeps it in the DOM, so the cookie settings link can
// reopen it.
function closeBanner() {
  hideBanner();
  hidePrefs();
  document.body.style.removeProperty("overflow");
}

function removeBanner() {
  cookieBanner?.remove();
  document.body.style.removeProperty("overflow");
}

function showPrefs() {
  cookieBannerPrefs?.style.setProperty("display", "block");
}

function removePrefs() {
  cookieBannerPrefs?.remove();
}

function setShopifyConsent(
  prefs = { analytics: false, marketing: false },
  onDone = () => {},
) {
  const customerPrivacy = window.Shopify?.customerPrivacy;

  if (isDevelopment() || !customerPrivacy?.setTrackingConsent) {
    console.debug(
      "[Customer Privacy API] - Skipped setShopifyConsent",
      { isDevelopment: isDevelopment() },
      prefs,
    );
    onDone();
  } else {
    customerPrivacy.setTrackingConsent(prefs, function () {
      console.info(
        "[Customer Privacy API] - Successfully setShopifyConsent",
        prefs,
      );
      onDone();
    });
  }
}

// Shopify keeps its own consent (_tracking_consent) for about a year, and the
// app embeds (Braze, Intelligems, ...) follow it. When our consent cookie is
// gone (never given, or expired), withdraw a consent Shopify still holds so
// those embeds stop with it. An existing consent cookie (e.g. from felmo.de)
// is synced into Shopify by snippets/consent-sync.liquid.
function resetShopifyConsent() {
  const customerPrivacy = window.Shopify?.customerPrivacy;

  if (
    !customerPrivacy?.analyticsProcessingAllowed?.() &&
    !customerPrivacy?.marketingAllowed?.()
  ) {
    return;
  }

  setShopifyConsent();
}

function loadCustomerPrivacyApi(onLoad = () => {}) {
  // The consent-tracking-api may already be loaded by another system on the
  // page (e.g. the Shogun optimizer snippet, which loads it in <head>).
  // Re-loading it triggers the "Multiple versions of Shopify.customerPrivacy
  // loaded" warning, so only load it if it isn't already available.
  if (window.Shopify?.customerPrivacy) {
    console.debug("[Customer Privacy API] - Already loaded, skipping");
    onLoad();
    return;
  }

  window.Shopify.loadFeatures(
    [
      {
        name: "consent-tracking-api",
        version: "0.1",
      },
    ],
    (error) => {
      if (error) {
        console.error("[Customer Privacy API] - Failed to load");
      } else {
        console.info("[Customer Privacy API] - Successfully loaded");
        onLoad();
      }
    },
  );
}

function bindBannerControls() {
  document
    .getElementById(COOKIE_BANNER_BUTTONS.ACCEPT)
    ?.addEventListener("click", function () {
      storeConsentSelected(true, { target: COOKIE_BANNER_BUTTONS.ACCEPT });
    });

  document
    .getElementById(COOKIE_BANNER_BUTTONS.DECLINE)
    ?.addEventListener("click", function () {
      storeConsentSelected(false, { target: COOKIE_BANNER_BUTTONS.DECLINE });
    });

  document
    .getElementById(COOKIE_BANNER_BUTTONS.PREFS)
    ?.addEventListener("click", function () {
      hideBanner();
      showPrefs();
    });

  document
    .getElementById(COOKIE_BANNER_BUTTONS.PREFS_ACCEPT)
    ?.addEventListener("click", function () {
      storeConsentSelected(true, {
        target: COOKIE_BANNER_BUTTONS.PREFS_ACCEPT,
      });
    });

  document
    .getElementById(COOKIE_BANNER_BUTTONS.PREFS_DECLINE)
    ?.addEventListener("click", function () {
      storeConsentSelected(false, {
        target: COOKIE_BANNER_BUTTONS.PREFS_DECLINE,
      });
    });

  document
    .getElementById(COOKIE_BANNER_PREFS_FORM)
    ?.addEventListener("submit", function (e) {
      e.preventDefault();

      const formElement = e.currentTarget;
      const statistics = formElement.statistics?.checked;
      const marketing = formElement.marketing?.checked;
      const isAccepted = !!statistics && !!marketing;

      storeConsentSelected(isAccepted, {
        target: COOKIE_BANNER_BUTTONS.PREFS_SAVE,
        prefs: { statistics, marketing },
      });
    });

  // Closing keeps the stored decision; the buttons are only shown on reopen.
  document
    .querySelectorAll(`.${COOKIE_BANNER_CLOSE_BUTTON}`)
    .forEach((button) => button.addEventListener("click", closeBanner));
}

function openCookieSettings() {
  const value = getCookie(COOKIE_CONSENT_NAME);

  // Without a decision the banner is either already open or about to be.
  const isOpen = [cookieBanner, cookieBannerPrefs].some(
    (element) => element?.style.display === "block",
  );

  if (!value || isOpen) {
    return;
  }

  console.debug("[Cookie Banner] - Reopening cookie banner from settings link");

  loadCustomerPrivacyApi(() => {
    const customerPrivacy = window.Shopify?.customerPrivacy;

    previousConsent = {
      analytics: !!customerPrivacy?.analyticsProcessingAllowed?.(),
      marketing: !!customerPrivacy?.marketingAllowed?.(),
      value,
    };

    // Pre-fill the preferences with what Shopify holds, which keeps the
    // statistics/marketing split the consent cookie collapses to all/none.
    const formElement = document.getElementById(COOKIE_BANNER_PREFS_FORM);

    if (formElement?.statistics) {
      formElement.statistics.checked = previousConsent.analytics;
    }

    if (formElement?.marketing) {
      formElement.marketing.checked = previousConsent.marketing;
    }

    document
      .querySelectorAll(`.${COOKIE_BANNER_CLOSE_BUTTON}`)
      .forEach((button) => button.removeAttribute("hidden"));

    showBanner();
    storeConsentShown({ reopened: true });
  });
}

function listenForCookieSettingsLinks() {
  document.addEventListener("click", function (e) {
    const link = e.target?.closest?.(`a[href$="${COOKIE_SETTINGS_HASH}"]`);

    if (!link) {
      return;
    }

    e.preventDefault();
    openCookieSettings();
  });

  if (window.location.hash === COOKIE_SETTINGS_HASH) {
    openCookieSettings();
  }
}

function initCookieBanner() {
  const utmMedium = getUtmParam("utm_medium");

  if (utmMedium === "site-test") {
    return;
  }

  const isCookieConsentSelected = getCookie(COOKIE_CONSENT_NAME);

  if (!cookieBanner) {
    console.debug(
      "[Cookie Banner] - No cookie banner element found in the DOM",
      {
        cookie_consent_selected: isCookieConsentSelected,
      },
    );

    return;
  }

  console.debug("[Cookie Banner] - Cookie banner element found in the DOM", {
    cookie_consent_selected: isCookieConsentSelected,
  });

  if (isOpenedFromApp()) {
    console.debug(
      "[Cookie Banner] - Opened from app. Removing cookie banner element...",
    );

    removeBanner();
    removePrefs();
    migrateLegacyCookieConsent();

    return;
  }

  bindBannerControls();
  listenForCookieSettingsLinks();

  if (isCookieConsentSelected) {
    console.debug("[Cookie Banner] - Cookie consent already selected");

    migrateLegacyCookieConsent();
  } else {
    console.debug(
      "[Cookie Banner] - Cookie consent not selected. Rendering cookie banner...",
    );

    loadCustomerPrivacyApi(resetShopifyConsent);
    showBanner();
    storeConsentShown();
  }
}

initCookieBanner();
